import React, { CSSProperties, useCallback, useRef } from 'react';
import classNames from 'classnames';
import InfiniteScroll from 'react-infinite-scroller';
import { get } from 'lodash';
import { DragEndEvent } from '@dnd-kit/core';

import Spinner from 'vibo-ui/Spinner';
import Draggable from 'components/common/Dnd/Draggable';
import DraggableContext from 'components/common/Dnd/DraggableContext';

import { reorderBatchByIds } from 'services/common/arrHelpers';
import { useSelectList } from 'components/context/SelectListContext';

import { ListProps } from './interfaces';

import useStyles from './style';

const List = <T extends WithId>({
  renderItem,
  onDropCallback,
  loading,
  className,
  dndType,
  dragByHandler,
  canDragHandler,
  children,
  gridProps,
  style,
  strategy,
  modifiers,
  scrollId,
  loadMore = () => {},
  hasMore = false,
  draggable = false,
  data = [],
  ...rest
}: ListProps<T>) => {
  const classes = useStyles();

  const listRef = useRef<HTMLDivElement>(null);

  const selectList = useSelectList();

  const renderInnerItem = (item: T, index: number) => {
    if (!renderItem) return null;

    const itemToRender = renderItem(item, index, {
      idx: index,
      element: data[index],
    });

    return itemToRender;
  };

  const preparedContent = data.map((item: T, index: number) => {
    const element = renderInnerItem(item, index);

    return (
      <React.Fragment key={(item as Partial<{ _id: string }>)?._id || index}>
        {element}
      </React.Fragment>
    );
  });

  const onDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (over && active.id !== over.id) {
        const selectedCount = selectList?.selectedIds?.length;
        const addedIndex = !!over.data.current?.sortable.index
          ? over.data.current?.sortable.index
          : -1;
        const removedIndex = active.data.current?.sortable.index;

        const newData = reorderBatchByIds({
          data,
          addedIndex,
          removedIndex,
          selectedIds: selectedCount ? selectList?.selectedIds : [data[removedIndex]._id],
        });

        const shift = (addedIndex ?? 0) < removedIndex ? 1 : 0;

        onDropCallback?.(newData, { removedIndex, addedIndex: addedIndex - shift });
      }
    },
    [data]
  );

  return (
    <DraggableContext
      dndType={dndType}
      modifiers={modifiers}
      strategy={strategy}
      onDragEnd={onDragEnd}
      preview={idx => preparedContent[idx]}
      items={data.map((option, idx) =>
        !canDragHandler ? option._id ?? idx : canDragHandler(option) ? option._id : ''
      )}
    >
      <InfiniteScroll
        pageStart={0}
        useWindow={false}
        loadMore={loadMore}
        hasMore={hasMore}
        loader={<Spinner className={classes.spinner} />}
        getScrollParent={() => scrollId && document.getElementById(scrollId)}
      >
        <div
          className={classNames('viboList', classes.viboList, className, {
            gridView: !!gridProps,
          })}
          {...rest}
          style={{
            ...(!!gridProps
              ? {
                  '--cols-sm': gridProps.columns[0],
                  '--cols-lg': gridProps.columns[1],
                  '--gap-sm': `${gridProps.gaps[0]}px`,
                  '--gap-lg': `${gridProps.gaps[1]}px`,
                }
              : ({} as CSSProperties)),
            ...style,
          }}
          ref={listRef}
        >
          {draggable
            ? data.map((option, idx) => {
                const itemId = get(option, '_id', idx.toString());
                const disabled = !!canDragHandler && !canDragHandler(option);

                return (
                  <Draggable
                    {...option}
                    id={`${dndType}-${itemId}`}
                    dragByHandler={dragByHandler}
                    disabled={disabled}
                    key={`dnd-list-item-${itemId}`}
                  >
                    {preparedContent[idx]}
                  </Draggable>
                );
              })
            : preparedContent}
          {children}
          {loading ? <Spinner className={classes.spinner} /> : null}
        </div>
      </InfiniteScroll>
    </DraggableContext>
  );
};

export default List;
